import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

/// Three-state theme controller. `mode` is the user's choice — "auto" follows the TIME OF DAY (dark
/// at night, light during the day), "light" / "dark" force a palette. `resolved` is the palette
/// actually in effect right now, which downstream consumers (e.g. RainbowKit, the toggle icon) key
/// off. The choice persists to localStorage for all three modes; with NO stored choice this app
/// defaults to LIGHT. document.documentElement.dataset.theme is always set to the resolved palette,
/// driving the CSS token layers in seafi-treasury.scss.

export type ThemeMode = 'auto' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'seafi-theme'

// Automatic = time of day. Dark from NIGHT_START (inclusive) to NIGHT_END (exclusive).
const NIGHT_START = 19 // 7pm
const NIGHT_END = 7 //   7am

export function isNightAt(date = new Date()): boolean {
  const h = date.getHours()
  return h >= NIGHT_START || h < NIGHT_END
}

type ThemeContextValue = {
  mode: ThemeMode
  resolved: ResolvedTheme
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function apply(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = resolved
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Default is LIGHT: with no stored choice the app opens light. Deterministic first render keeps
  // hydration stable; the inline FOUC script already painted it, and the effects below sync the
  // stored choice (auto/light/dark).
  const [mode, setModeState] = useState<ThemeMode>('light')
  const [night, setNight] = useState<boolean>(false)

  // Hydrate the stored choice and start the time-of-day clock (re-checks each minute so "auto" flips
  // at the day/night boundary while the app stays open).
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'auto' || stored === 'light' || stored === 'dark') setModeState(stored)
    setNight(isNightAt())
    const id = window.setInterval(() => setNight(isNightAt()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  const resolved: ResolvedTheme = mode === 'auto' ? (night ? 'dark' : 'light') : mode

  // Keep the DOM attribute in sync with the resolved palette — but skip the very first commit, whose
  // deterministic default could momentarily override the FOUC script's correct pre-paint value.
  const first = useRef(true)
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    apply(resolved)
  }, [resolved])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    localStorage.setItem(STORAGE_KEY, next)
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
